/* eslint-disable no-extend-native */

// =============================================================================
// Utilities
// =============================================================================

import {
    allPass,
    append,
    equals,
    find,
    is,
    isEmpty,
    join,
    findIndex,
    keys,
    pathEq,
    pipe,
    pluck,
    prop,
    range,
    reject,
    take as strTake,
    trim,
    values,
    without,
    when,
    zipObj,
} from 'ramda'
import { Ref, ref } from 'vue'

// Model Query Helpers
// ----------------------------------------------------------------------------

// Shorthand queries to pass to find/filter
export const byId = pathEq(['id'])
export const byMenuOption = pathEq(['menu_option_id'])
export const byRouteTable = pathEq(['route_table_id'])
export const railsModel = prop('railsModel')
export const byPriority = (a: any, b: any) => a.priority - b.priority
export const byOrder = (a: any, b: any) => a.order - b.order
export const byWeight = (a: any, b: any) => {
    const aWeight = a.locked ? a.target.weight : a.weight
    const bWeight = b.locked ? b.target.weight : b.weight
    return bWeight - aWeight
}

/**
 * take
 * A better very of pluck with nil checks and removing undefined list items
 */
export const take = (_prop: string, arr: any[]): any[] => {
    if (isEmpty(arr)) return []
    return reject(equals(undefined), pluck(_prop, arr))
}

/**
 * propFilters
 * Generate a list of functions to pass to find()/filter() an array of objects by
 * matching all of the properties/values within the supplied object
 * @param {Object} obj - Properies/values which to filter an array of objects by
 */
const propFilters = (obj: any) => {
    const fns = keys(obj).map((k: any) => pathEq(k.split('.'), obj[k]))
    return allPass(fns)
}

/**
 * where
 * Filter an array using a hash of properties. Nest properties can be queried
 * using dot notation.
 * @example
 *
 *      const arr = [{ a: 1, b: 'two' }, {a: 1, b: 'three' }, { a: 1, b: 'two' }]
 *      where({
 *          a: 1,
 *          b: 'two'
 *      }, arr) -> [{ a: 1, b: 'two' }, { a: 1, b: 'two' }]
 *
 */
export const where = function Where(obj: any, arr: any[], action = 'filter'): any[] {
    // @ts-ignore
    return (arr || this)[action](propFilters(obj))
}

/**
 * findWhere
 * Same as `where` above but using 'find' instead of filter
 */
export const findWhere = function findWhere(obj: any, arr: any[]): any[] {
    // @ts-ignore
    return where(obj, arr || this, 'find')
}

/**
 * rejectWhere
 * Same as `where` above but rejecting matches from the array instead of
 * filtering down to just matches.
 */
const rejectWhere = function rejectWhere(obj: any, arr: any[]): any[] {
    // @ts-ignore
    return reject(propFilters(obj), arr || this)
}

/**
 * pluck
 * Returns a new list by plucking the same named property off all objects in the list supplied.
 */
export const pluckKey = function pluckKey(key: string, arr: any[]): any[] {
    // @ts-ignore
    return pluck(key, arr || this)
}

/**
 * railsModel
 * Fetch all railsModel from each object and filter out the non-persisted deleted
 */
const railsModels = function railsModels(): any[] {
    return reject((obj: any) => !obj.persisted && obj._destroy, take('railsModel', this))
}

/**
 * Take helpers
 */
export const ids = function ids(arr: any): number[] {
    return take('id', arr)
}
export const weights = function weights(arr: any): number[] {
    return take('weight', arr)
}
export const models = function models(arr: any): any[] {
    return take('model', arr)
}

/**
 * Return the first item in the list thats not null or undefined
 */
export const firstNotNull = find((val) => val !== undefined && val !== null)

// Helpers
// ----------------------------------------------------------------------------

/**
 * A better interface for setTimeout
 * @param {Number} ms
 * @param {Function} fn
 * @return {void}
 */
export const delay = (ms: number, fn: () => any): void => {
    setTimeout(() => {
        fn()
    }, ms)
}

/**
 * Run an async function after 1 millisecond to clear process queues
 * @param {Function} fn
 * @return {Promise}
 */
export const after1ms = (fn: () => any): Promise<any> =>
    new Promise((resolve) => {
        delay(1, () => resolve(fn()))
    })

/**
 * Retry a function n number of times every 25ms
 * @param {Function} fn
 * @param {Function} n - Number of times to retry function
 * @return {Promise}
 */
export const repeat = (fn: () => any, n = 5, step = 25): void => {
    range(0, n).forEach((count: number) => {
        const time = (count + 1) * step
        delay(time, () => fn())
    })
}

/**
 * Generate a random number between a range
 * @param {Number} min - Minimum range
 * @param {Number} max - Maximum range
 * @return {Number}
 */
export const randomNumber = (min = 1, max = 1000): number =>
    Math.floor(Math.random() * (max - min + 1)) + min

/**
 * Generate unique id
 */
export const uuid = (): number => {
    const min = 1000000000
    const max = 1999999999
    return randomNumber(min, max)
}

export const selectRandomRange = (arr: any[], max?: number): any[] => {
    const l: number = max || arr.length
    const x: number = randomNumber(0, l)
    const y: number = randomNumber(0, l)
    return arr.slice(x <= y ? x : y, y >= x ? y : x)
}

/**
 * Check if property is present on object
 */
export const isPresent = (attribute?: string): boolean => {
    if (!attribute || isEmpty(attribute)) {
        return false
    }
    return JSON.stringify(attribute).length > 0
}

/**
 * Converts any property into an array
 */
export const arrayify = (arr: any): any[] => {
    if (is(Array, arr)) return arr
    return [arr]
}

/**
 * Conver a comma-seperated string into an array
 */
export const arrayStringify = (arr: string): string[] => {
    if (arr) return arr.split(',')
    return []
}

/**
 * Array of objects cloning
 */
export const cloneArr = (arr: any[]) => arr.slice(0).map((i: any) => ({ ...i }))

/**
 * Check if mobile device
 */
export const isMobileDevice = (): boolean =>
    window.matchMedia('screen and (max-width: 480px)').matches

/**
 * Return an uppercase string
 */
export const uppercase = (value: string): string => {
    if (!value) return ''
    return value.toString().toUpperCase()
}

/**
 * Return capitalized string
 */
export const capitalize = (value: string): string => {
    if (!value) return ''
    value = value.toString()
    return value.charAt(0).toUpperCase() + value.slice(1)
}

/**
 * Return singular of plural string
 */
export const singular = (str: string): string => {
    if (str.slice(str.length - 1, str.length)) {
        return str.slice(0, str.length - 1)
    }
    return str
}

/**
 * Return capitalized version of string with underscores removed and singularized
 */
export const humanize = (str: string): string => {
    const frags = str.split('_').map((f) => f.charAt(0).toUpperCase() + f.slice(1))
    return singular(frags.join(''))
}

/**
 * Return lowercase string
 */
export const lowercase = (value: string): string => {
    if (!value) return ''
    return value.toString().toLowerCase()
}

/**
 * Format phone numbers using common US format
 * "2345678900" -> "(234) 567-8900"
 * "+12345678900" -> "+1 (234) 567-8900"
 * Returns the same string if it's not a valid phone number
 * "123" -> "123"
 */
export const formatPhoneNumber = (phoneNumberString: string): string => {
    const cleaned = `${phoneNumberString}`.replace(/\D/g, '')
    const match = cleaned.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/)
    if (match) {
        const intlCode = match[1] ? '+1 ' : ''
        return [intlCode, '(', match[2], ') ', match[3], '-', match[4]].join('')
    }
    return phoneNumberString
}

/**
 * Reverse an object by swapping the keys and values
 */
export const reverse = (obj: any) => {
    const ks = keys(obj)
    const vs = values(obj)
    return zipObj(vs, ks)
}

/**
 * Convert string or number reliably into number
 */
export const toNum = (n: string | number): number => parseInt(`${n}`, 0)

/**
 * Poll function until it returns true
 */
export const poll = (fuction: () => boolean) =>
    new Promise((resolve) => {
        const interval = setInterval(() => {
            if (fuction()) {
                clearInterval(interval)
                resolve(true)
            }
        }, 100)
    })

// Pinia/VueX utilities
// ----------------------------------------------------------------------------

/**
 * A reactive-friendly way to destroy a list of IDs from a state tree containg an
 * array of objects
 * @param {Array} _ids
 * @param {Object<string, any>} state
 */
export const destroyIds = (_ids: any, state: any) => {
    arrayify(_ids).forEach((id) => {
        const index = findIndex(byId(id))(state.collection)
        state.collection.splice(index, 1)
    })
}

/**
 * A reactive-friendly way to safely mark a list of objects as _destroy=true
 * @param {String} _prop - optional property on state object
 */
export const safeDestroyIds = (_ids: number | number[], state: any, _prop = 'collection') => {
    arrayify(_ids).forEach((id) => {
        const obj = state[_prop].find(byId(id))
        obj.destroy()
    })
}

/**
 * A reactive-friendly way to replace an object inline (minus a few properies) without
 * having to delete and replace the whole object.
 * @param {Number} id
 * @param {Object<string, any>} state
 */
export const replaceId = (id: any, el: any, state: any) => {
    const obj = state.collection.find(byId(id))
    obj.loader = false
    without(['loader', 'route_table_id', 'order', 'id'], keys(el)).forEach((k: any) => {
        obj[k] = el[k]
    })
}

/**
 * Apply defaults from one object to another, when properties are
 * undefined or null
 */
export const defaultOptions = (defaults: any, options: any): any => {
    const newObject = {}
    Object.keys(options).forEach((key) => {
        newObject[key] = options[key] || options[key] === 0 ? options[key] : defaults[key]
    })
    return newObject
}

// Date utilities
// ----------------------------------------------------------------------------

function timeDate(hour: string | number, min: string | number): Date {
    const d = new Date()

    return new Date(d.getFullYear(), d.getMonth(), d.getDate(), toNum(hour), toNum(min))
}

function timeString(time: number | string): string {
    const timeN = parseInt(`${time}`)
    if (timeN === 0) {
        return '000'
    }
    if (timeN < 60) {
        return `0${time}`
    }
    return `${time}`
}

/**
 * Convert number string to two digits
 */
export const twoDigits = (n: number | string): string => {
    const num = `${n}`.split('.')[0]
    return `0${num}`.slice(-2)
}

export const hour = (n: number | string): string => `${n}`.slice(0, -2)

export const minutes = (n: any): string => {
    const ts = `${n}`
    return ts.slice(ts.length - 2, ts.length)
}

// Convert js Date to time (ie, 900)
export const dateToTime = (date: Date): number => {
    const tomorrow = (d: any) => d.getDate() > new Date().getDate()
    const hr = date.getHours()
    const mn = twoDigits(date.getMinutes())
    if (tomorrow(date) && hr === 0) {
        return 2400
    }
    return toNum(`${hr}${mn}`)
}

// Convert numerical time into js Date
export const timeToDate = (time: number): Date => {
    if (time === 2400) {
        return timeDate(23, 59)
    }
    const ts = timeString(time)
    return timeDate(hour(ts), minutes(ts))
}

// Convert time string ("9:00") to numerical (900)
export const stringToTime = (ts: string): number => {
    if (!ts) return 0
    const time = ts.split(':')
    return toNum(`${time[0]}${time[1]}`)
}

// Convert time string ("900") to formatted time ("9:00")
export const prettyTime = (time: number): string => {
    if (!time) return '0:00'
    const ts = timeString(time)
    const l = ts.length
    return `${ts.slice(0, l - 2)}:${ts.slice(l - 2)}`
}

/**
 * Days between two dates
 */
export const daysBetween = (date1: Date, date2: Date): number => {
    const oneDay = 24 * 60 * 60 * 1000 // hours*minutes*seconds*milliseconds
    return Math.round(Math.abs((date1.getTime() - date2.getTime()) / oneDay))
}

/**
 * Check if days between range is over n limit
 */
export const daysBetweenOverLimit = (
    dateRange: [Date | string, Date | string],
    limit: number,
): boolean => {
    const [from, to] = dateRange
    return daysBetween(new Date(from), new Date(to)) > limit
}

/**
 * Add 15 minutes to supplied (numerical) time
 */
export const add15Min = (time: number): number => {
    if (time + 15 > 2359) {
        return 2400
    }
    const ts = timeString(time)
    if (minutes(ts) === '45') {
        return time + 100 - 45
    }
    return time + 15
}

export const add30Min = (time: number): number => {
    if (time + 30 >= 2359) {
        return 2400
    }
    return add15Min(add15Min(time))
}

export const minus15Min = (time: number): number => {
    if (time - 15 < 0) {
        return 0
    }
    const ts = timeString(time)
    if (minutes(ts) === '00') {
        return time - 100 + 45
    }
    return time - 15
}

export const minus45Min = (time: number): number => {
    if (time - 15 < 0) {
        return 0
    }
    return minus15Min(minus15Min(minus15Min(time)))
}

export const fourDigit = (ts: number): number[] => [toNum(hour(ts)), toNum(minutes(ts))]

export const singleDigit = (ts: string): number[] => [toNum(ts), 0]

export const splitColon = (ts: string): number[] => {
    const sp = ts.split(':')

    return [toNum(sp[0]), toNum(sp[1])]
}

// Convert numerical time (900) to string with colon ("9:00")
export const addColon = (tn: number): string => {
    const ts = `${tn}`
    const hr = hour(ts)
    const mn = minutes(ts)
    return `${hr === '' ? 0 : hr}:${twoDigits(mn)}`
}

// Converts any time (900/"900"/"9:00"/"9am") into a 24hr time
export const parseTimeInput = (val: string): number => {
    let ts = is(String, val) ? trim(val) : `${val}`
    let hr: string | number
    let mn: string | number
    if (ts === 'noon') ts = '12:00'
    if (ts === 'midnight') ts = '24:00'
    if (/am|pm/.test(ts)) {
        if (!ts.includes('pm')) {
            ts = ts.replace('am', '')
            if (ts.includes(':')) {
                ;[hr, mn] = splitColon(ts)
            } else if (ts.length > 2) {
                ;[hr, mn] = fourDigit(parseInt(ts, 0))
            } else {
                ;[hr, mn] = singleDigit(ts)
            }
            if (hr === 12 && mn > 0) hr = ''
            if (hr === 12 && mn === 0) hr = '24'
        } else {
            ts = ts.replace('pm', '')
            if (ts.includes(':')) {
                ;[hr, mn] = splitColon(ts)
            } else if (ts.length > 2) {
                ;[hr, mn] = fourDigit(parseInt(ts, 0))
            } else {
                ;[hr, mn] = singleDigit(ts)
            }
            hr += 12
            if (hr === 24) hr = '12'
        }
        return toNum(`${hr}${twoDigits(mn)}`)
    }
    return toNum(ts.replace(':', ''))
}

/**
 * Validates time is 1-4 digits long and between 0 and 2400
 */
export const validTime = (tn: number): boolean => {
    if (`${tn}`.length > 4) return false
    return tn >= 0 && tn <= 2400
}

/**
 * Validates time range values
 */
export const validTimeRange = (from: number, to: number): boolean =>
    validTime(from) && validTime(to) && to > from

/**
 * Count the number of digits in a number. For example ten = 2, one hundred =
 * 3, a thousand = 4, etc
 */
export const countDigits = (n: number): number => {
    if (!is(Number, n)) return 0
    return `${n}`.length
}

// Misc utilities
// ----------------------------------------------------------------------------

/**
 * Decimal adjustment of a number.
 */
export const round10 = (value: any, exponent: number) => {
    const e = 10 ** exponent
    return Math.round(value * e) / e
}

/**
 * Returns true if Safari browser is being used
 */
export const isSafari = () => /^((?!chrome|android|crios|fxios).)*safari/i.test(navigator.userAgent)

/**
 * Truncate a string to max number
 */
export const truncate = (str: string | number, n: number): string => {
    const val = `${str}`
    return when(
        () => val.length > n,
        pipe(strTake(n), append('…'), join('')),
        // @ts-ignore: when does indeed take a string
    )(val) as string
}

/**
 * Run function after user finishes typing after [n] milliseconds
 */
export const whenDoneTyping = (el: string, ms: number, fn: () => any) => {
    let typingTimer
    const input: HTMLInputElement | null = document.querySelector(el)

    if (input) {
        input.addEventListener('keyup', () => {
            clearTimeout(typingTimer)
            typingTimer = setTimeout(() => {
                fn()
            }, ms)
        })
    }
}

// Vue helpers
// ----------------------------------------------------------------------------

/**
 * Wraps each value in object with Ref
 */
export const toRef = (object: object) => {
    const refs = {}
    Object.keys(object).forEach((k) => {
        refs[k] = ref(object[k])
    })
    return refs
}

/**
 * Remove Ref from each value in object
 */
export const deRef = (object: object) => {
    const refs = {}
    Object.keys(object).forEach((k) => {
        refs[k] = object[k].value
    })
    return refs
}

/**
 * Unwrap a ref without causing an error
 */
export const unwrapRef = (obj: Ref<any> | any) => {
    try {
        return typeof obj.value !== 'undefined' ? obj.value : obj
    } catch {
        return obj
    }
}

// Monkey patching
// ----------------------------------------------------------------------------

declare global {
    interface Array<T> {
        where(o: Partial<T>): Array<T>
        findWhere(o: Partial<T>): T
        rejectWhere(o: Partial<T>): Array<T>
        railsModels(): Array<T>
        ids(): Array<T>
        weights(): Array<T>
        models(): Array<T>
        flat(): Array<T>
        pluck(): Array<T>
    }
}

export const monkeyPatch = function monkeyPatch() {
    if (typeof Array.prototype.where !== 'undefined') return
    Object.defineProperty(String.prototype, 'capitalize', {
        value() {
            return this.charAt(0).toUpperCase() + this.slice(1)
        },
        enumerable: false,
    })
    Object.defineProperty(Array.prototype, 'where', {
        value: where,
    })
    Object.defineProperty(Array.prototype, 'findWhere', {
        value: findWhere,
    })
    Object.defineProperty(Array.prototype, 'rejectWhere', {
        value: rejectWhere,
    })
    Object.defineProperty(Array.prototype, 'pluck', {
        value: pluckKey,
    })
    Object.defineProperty(Array.prototype, 'railsModels', {
        value: railsModels,
    })
    Object.defineProperty(Array.prototype, 'ids', {
        value() {
            return take('id', this)
        },
    })
    Object.defineProperty(Array.prototype, 'weights', {
        value() {
            return take('weight', this)
        },
    })
    Object.defineProperty(Array.prototype, 'models', {
        value() {
            return take('model', this)
        },
    })

    window.log_disabled = true
    window.log = window.log_disabled ? () => null : console.log
}
